<template>
  <b-card class="border mt-1 mb-1">
    <div v-if="this.logo.src">
      <div class="row">
        <Cropper ref="cropper" class="cropper-logo" :src="logo.src" stencil-component="circle-stencil"></Cropper>
        <div class="btn-group d-flex w-100 mt-2" role="group">
          <div class="btn btn-secondary btn-sm" @click="resetCropper">Cancel</div>
          <div class="btn btn-warning btn-sm" @click="rotate">Rotate</div>
          <div class="btn btn-primary btn-sm" @click="onProjectLogoSelected">Crop</div>
        </div>
      </div>
    </div>

    <div v-else class="row justify-content-center">
      <i v-if="isLoading" class="fa fa-3x fa-spin fa-spinner ml-1 mt-2"></i>
      <div v-else class="rounded-sm profile-background p-4 mb-2">
        <b-img rounded="circle" thumbnail fluid alt="Project Logo" :src="projectLogo"/>
      </div>
      <div class="col-12">
        <div class="row justify-content-center">
          <VueUploadComponent
            v-if="!logoPresent"
            v-bind="fileUploadOptions"
            v-model='fileProjectLogo'
            @input-filter="inputFilter"
            ref="uploadLogo"
            input-id="logo-upload-input"
            name="logo-upload"
            alt="Add a project logo"
            title="Add a project logo"
            class="btn btn-primary mb-1"
          >
            <span><i class="fa fa-plus" aria-hidden="true" title="Add a project logo"></i> <b>Add Logo</b></span>
          </VueUploadComponent>
          <div v-else>
            <VueUploadComponent
              v-bind="fileUploadOptions"
              v-model='fileProjectLogo'
              @input-filter="inputFilter"
              ref="uploadLogo"
              input-id="logo-upload-input"
              name="logo-upload"
              alt="Change project logo"
              title="Change project logo"
              class='btn btn-secondary'
            >
              <span><i class="fa fa-pencil" aria-hidden="true" title="Change project logo"></i> Change</span>
            </VueUploadComponent>
            <b-btn class="ml-1" variant="danger" size="md" title="Remove project logo"
                   @click="removeProjectLogo">
              <span><i class="fa fa-trash" aria-hidden="true" title="Remove project logo"></i> Remove</span>
            </b-btn>
          </div>
        </div>
      </div>
      <div class='text-danger' v-if='fileTypeIssue'>Project logo must be a .jpg, .jpeg, or .png</div>
      <div class='text-danger' v-if='fileSizeIssue'>Project logo must be less than 10MB</div>
    </div>
  </b-card>
</template>

<script>

import {mapGetters, mapActions} from 'vuex'
import VueUploadComponent from 'vue-upload-component'
import {Cropper} from 'vue-advanced-cropper';
import 'vue-advanced-cropper/dist/style.css';

export default {
  name: 'EditProjectLogo',
  components: {
    VueUploadComponent,
    Cropper,
  },
  data() {
    return {
      rounded: false,
      fileUploadOptions: {
        multiple: false,
        drop: true,
        accept: this.projectLogoAccept,
        extensions: this.projectLogoExtensions,
        size: 1024 * 1024 * 10,
        valueDeterminate: 100,
        imageUploadedSuccessfully: false,
        showUploadedImage: 0
      },
      fileProjectLogo: [],
      fileTypeIssue: false,
      fileSizeIssue: false,
      logo: {
        src: null,
        type: null
      },
      isLoading: false,
    }
  },

  created() {
  },

  watch: {
    fileProjectLogo(newVal, oldVal) {
      if (newVal) {
        // If a file is uploaded, put it through the cropper
        if (newVal[0]) {
          if (this.logo.src) {
            URL.revokeObjectURL(this.logo.src)
          }
          // 2. Create the blob link to the file to optimize performance:
          const blob = URL.createObjectURL(newVal[0].file);

          // Create a new FileReader to read this image binary data
          const reader = new FileReader();
          // Define a callback function to run, when FileReader finishes its job
          reader.onload = (e) => {
            this.logo = {
              // Set the image source
              src: blob,
              // Determine the image type
              type: newVal[0].type,
            };
          };
          // Start the reader job - read file as a data url (base64 format)
          reader.readAsArrayBuffer(newVal[0].file);
        }
      }
    },
    uploadLogoStatus(newVal) {
      // Cropper is reset on upload
      if (newVal && newVal.type === this.projectLogoType) {
        this.resetCropper()
        let currentStatus = newVal.status
        if (currentStatus === 'SUCCESS') {
          this.isLoading = false
        } else {
          this.isLoading = currentStatus === 'IS_UPLOADING';
          if (!this.isLoading) {
            // Handle errors here
          }
        }
      }
    },
  },
  computed: {
    ...mapGetters({
      projectVuex: 'project/get/project',
      projectLogoType: 'constants/projectLogoType',
      projectLogoAccept: 'constants/projectLogoAccept',
      projectLogoExtensions: 'constants/projectLogoExtensions',
      uploadLogoStatus: "project/images/upload",
    }),
    projectLogo() {
      return (this.projectVuex && this.projectVuex.picture && this.projectVuex.picture.path)
        ? this.projectVuex.picture.path
        : '/assets/images/PoweredByCitSci.png';
    },
    logoPresent() {
      return this.projectLogo !== '/assets/images/PoweredByCitSci.png'
    },
  },
  methods: {
    ...mapActions({
      uploadLogo: "project/images/uploadImage",
      updateProject: "project/update/updateProject",
    }),
    inputFilter(newFile, oldFile, prevent) {
      if (newFile && !oldFile) {
        let fileTypeIssue = false;
        let fileSizeIssue = false;
        if (!/\.(jpg|jpeg|png|webp)$/i.test(newFile.name)) {
          fileTypeIssue = true;
        }
        if (newFile.size > 10 * 1000000) {
          fileSizeIssue = true;

        }
        if (fileTypeIssue || fileSizeIssue) {
          this.fileTypeIssue = (fileTypeIssue || this.fileTypeIssue);
          this.fileSizeIssue = (fileSizeIssue || this.fileSizeIssue);
          return prevent()
        }
      }
      if (newFile && (!oldFile || newFile.file !== oldFile.file)) {
      }
    },
    onProjectLogoSelected() {
      const {canvas} = this.$refs.cropper.getResult();
      if (canvas) {
        canvas.toBlob(blob => {
          this.uploadLogo({
            blob: blob,
            name: this.fileProjectLogo[0].name,
            type: this.projectLogoType,
          })
        });
      }
    },
    rotate() {
      this.$refs.cropper.rotate(90);
    },
    resetCropper() {
      this.logo.src = null
      this.logo.type = null
    },
    removeProjectLogo() {
      this.updateProject({project: {picture: null}})
    },
  },
}
</script>

<style scoped lang="scss">

.profile-background {
  background-color: #C0C0C0;
}

.cropper-logo {
  max-height: 350px;
  width: 100%;
  background-color: #DDD;
}
</style>
